/**
 * ResetPassword Component
 * Reset password using reset token from email
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { userManagementAPI } from '@/services/api/userManagement';
import { Button } from '../ui/button';

import { useAppTranslation } from '@/contexts/TranslationContext';
export const ResetPassword: React.FC = () => {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!resetToken) {
      setError('Invalid or missing reset token');
    }
  }, [resetToken]);

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: '#dc2626' };
    if (score <= 3) return { score, label: 'Fair', color: '#ea580c' };
    if (score <= 4) return { score, label: 'Good', color: '#ca8a04' };
    return { score, label: 'Strong', color: '#16a34a' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!resetToken) {
      setError('Invalid reset token');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      await userManagementAPI.resetPassword(resetToken, formData.password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login?reset=true');
      }, 2000);
    } catch (err: any) {
      console.error('Password reset failed:', err);
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
      setSubmitting(false);
    }
  };

  if (!resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-8 md:p-10 w-full max-w-md shadow-2xl text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-5" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.invalid_reset_link', 'Invalid Reset Link')}</h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mb-6">
            {t('auth.this_password_reset_link_is', 'This password reset link is invalid or has expired.')}
          </p>
          <Button
            onClick={() => navigate('/forgot-password')}
            variant="primary"
            fullWidth
          >
            {t('auth.request_new_link', 'Request New Link')}
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-8 md:p-10 w-full max-w-md shadow-2xl text-center">
          <div className="w-16 h-16 mx-auto mb-5 bg-green-500 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.password_reset_successfully', 'Password Reset Successfully!')}</h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{t('auth.redirecting_you_to_login', 'Redirecting you to login...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-600 to-purple-700 p-4 md:p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Lock className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.set_new_password', 'Set New Password')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">{t('auth.enter_a_strong_password_for', 'Enter a strong password for your account')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Lock className="w-4 h-4" />
              <span>{t('auth.new_password', 'New Password')}</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={t('auth.enter_a_strong_password', 'Enter a strong password')}
                required
                disabled={submitting}
                className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  />
                </div>
                <span className="text-xs md:text-sm font-medium" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Lock className="w-4 h-4" />
              <span>{t('auth.confirm_new_password', 'Confirm New Password')}</span>
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder={t('auth.reenter_your_password', 'Re-enter your password')}
                required
                disabled={submitting}
                className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <small className="block mt-1 text-xs text-red-600 dark:text-red-400">{t('auth.passwords_do_not_match', 'Passwords do not match')}</small>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={submitting}
            loading={submitting}
            className="mt-6"
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};
